import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Plus, Loader2, Power, FileText, Users, CalendarX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_BADGE = {
  compliant: { label: "Compliant", cls: "bg-[#E6F0EB] text-[#34745C]" },
  needs_attention: { label: "Needs attention", cls: "bg-[#FDF3E1] text-[#C48231]" },
  at_risk: { label: "At risk", cls: "bg-[#FCEAE8] text-[#B54D40]" },
};

const EMPTY = { name: "", company: "", email: "", password: "" };

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]";

const Metric = ({ icon: Icon, label, bad }) => (
  <span className={`inline-flex items-center gap-1 text-xs ${bad ? "text-[#B54D40] font-semibold" : "text-[#57605A]"}`}>
    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
  </span>
);

export default function SubcontractorsPage() {
  const { user } = useAuth();
  const [subs, setSubs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/subcontractors").then((r) => setSubs(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  if (user.role !== "agency" && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/subcontractors", form);
      toast.success(`Subcontractor account created — share the login (${form.email}) with them securely.`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create subcontractor");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/subcontractors/${s.id}`, { active: !s.active });
      toast.success(s.active ? `${s.name} deactivated` : `${s.name} reactivated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <Layout>
      <div data-testid="subcontractors-page" className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Subcontractor Network</p>
            <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
              Your <span className="font-bold text-[#2E5C50]">subcontractors</span>
            </h1>
            <p className="mt-2 text-[#57605A] max-w-2xl">
              Each subcontractor gets their own portal with separate documents, caregivers and visits.
              Nexari rolls their compliance up to you automatically.
            </p>
          </div>
          <button
            data-testid="add-subcontractor-btn"
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add subcontractor
          </button>
        </div>

        {subs === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
          </div>
        ) : subs.length === 0 ? (
          <div data-testid="empty-subcontractors" className="bg-white border border-[#E8E6E1] rounded-lg p-12 text-center">
            <Building2 className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-[#57605A]">No subcontractors yet. Create an account for your first subcontractor agency.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s, i) => {
              const c = s.compliance;
              const badge = STATUS_BADGE[c.status] || STATUS_BADGE.needs_attention;
              return (
                <div
                  key={s.id}
                  data-testid={`sub-row-${s.id}`}
                  className={`fade-in-up bg-white border border-[#E8E6E1] rounded-lg p-5 flex flex-wrap items-center gap-4 ${!s.active ? "opacity-60" : ""}`}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="w-10 h-10 bg-[#D6E0D9]/40 rounded-md flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1C211F]">
                      {s.name}
                      {s.company && <span className="text-[#57605A] font-normal"> · {s.company}</span>}
                      {!s.active && <span className="ml-2 text-xs font-semibold text-[#B54D40] uppercase">Deactivated</span>}
                    </p>
                    <p className="text-sm text-[#57605A]">{s.email}</p>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                      <Metric icon={FileText} bad={c.documents.high_risk > 0}
                        label={`${c.documents.total} docs · ${c.documents.high_risk} high risk · ${c.documents.pending_review} pending`} />
                      <Metric icon={Users} bad={c.caregivers.expired > 0}
                        label={`${c.caregivers.total} caregivers · ${c.caregivers.expired} expired · ${c.caregivers.expiring} expiring`} />
                      <Metric icon={CalendarX} bad={c.visits.missed > 0 || c.visits.evv_flagged > 0}
                        label={`${c.visits.missed} missed visits · ${c.visits.evv_flagged} EVV flagged`} />
                    </div>
                  </div>
                  <span data-testid={`sub-status-${s.id}`} className={`${badge.cls} px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider whitespace-nowrap`}>
                    {badge.label}
                  </span>
                  <button
                    data-testid={`toggle-active-${s.id}`}
                    onClick={() => toggleActive(s)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-colors duration-200 ${
                      s.active ? "bg-[#FCEAE8] text-[#B54D40] hover:bg-[#F8D8D4]" : "bg-[#E6F0EB] text-[#34745C] hover:bg-[#D6E8DE]"
                    }`}
                  >
                    <Power className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {s.active ? "Deactivate" : "Reactivate"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add subcontractor</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 mt-2">
            <input data-testid="sub-name-input" required placeholder="Contact name" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="sub-company-input" placeholder="Company name" value={form.company} onChange={set("company")} className={inputCls} />
            <input data-testid="sub-email-input" required type="email" placeholder="Login email" value={form.email} onChange={set("email")} className={inputCls} />
            <input data-testid="sub-password-input" required type="text" minLength={6} placeholder="Temporary password (min 6 characters)" value={form.password} onChange={set("password")} className={inputCls} />
            <p className="text-xs text-[#57605A]">
              They'll sign in with these credentials and get their own portal — separate documents, caregivers and visit schedules. You'll see their compliance status here.
            </p>
            <button
              data-testid="save-subcontractor-btn"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              Create account
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
